import { memo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { callGetGroupApi, GroupWithLoginUser, GroupType } from 'requests/RequestGroup';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import { HttpStatus } from 'consts/HttpStatus';
import { Hr } from 'components/atoms/Hr';
import { findGroupAuthByKey, isAdmin, isMember, GroupAuth } from 'consts/GroupAuthConst';
import { BirthDay } from 'components/molecules/BirthDay';

type Props = {};

export const FamilyInfoTemplate = memo((_props: Props) => {
	const [store] = useStore();

	// 家族情報を取得する
	const [groupData, setGroupData] = useState<GroupWithLoginUser[]>([]);
	const [getGroupLoading, setGetGroupLoading] = useState(false);
	useEffect(() => {
		const execute = async () => {
			setGetGroupLoading(true);
			const getGroupResult = await callGetGroupApi(GroupType.FAMILY);
			if (!!store.loginUserData.gidpk && getGroupResult.status === HttpStatus.OK && !!getGroupResult.data) {
				if (getGroupResult.data.group_list.length === 0) {
					setGroupData([]);
					setGetGroupLoading(false);
					return;
				}
				// ログインユーザのグループごとの情報をグループのヘッダに持つ
				const groupWithLoginUsers = getGroupResult.data.group_list.map((g) => {
					return { ...g, loginUser: g.gid_list.find((u) => u.gidpk === store.loginUserData.gidpk) };
				});
				setGroupData(groupWithLoginUsers);
			}
			setGetGroupLoading(false);
		};
		execute();
	}, []);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDマイページ｜家族情報';
	}, []);

	return (
		<>
			<Backdrop className={classes.backdrop} open={getGroupLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>家族情報</li>
					</ul>
				</div>

				<h1 className="page-head">家族情報</h1>

				{groupData.length > 0 ? (
					<>
						<div className={classes.changeMember}>
							{isAdmin(findGroupAuthByKey(groupData[0].loginUser)) && (
								<Link
									to={{
										pathname: RoutePath.CHANGE_REPRESENTATIVE,
										state: { group: groupData[0] },
									}}
								>
									<span className={classes.changeMemberSpan}>代表者を変更する</span>
								</Link>
							)}
						</div>
						<div className="section-in">
							<div className="section-head">
								<span className={classes.sectionHeadH1}>「家族」 一覧</span>
							</div>
							<div className="info-box">
								<div className={classes.memberRow}>
									<div className={`${classes.memberRowHeadColumn} ${classes.iconColumn}`} />
									<div className={`${classes.memberRowHeadColumn} ${classes.nameColumn}`}>お名前</div>
									<div className={`${classes.memberRowHeadColumn} ${classes.birthColumn}`}>生年月日</div>
									<div className={`${classes.memberRowHeadColumn} ${classes.linkColumn}`} />
								</div>
								<Hr />
								{groupData[0].gid_list.map((u) => {
									const auth = findGroupAuthByKey(u);
									return (
										<>
											<div className={classes.memberRow}>
												<div
													className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}
												>
													<img className={classes.memberIcon} src={`images/${auth.image}`} alt="" />
													<div className={classes.memberLabel}>{auth.name}</div>
												</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.nameColumn} ${classes.nameDataColumn}`}
												>{`${u.lastname}　${u.firstname}`}</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.birthColumn} ${classes.birthDataColumn}`}
												>
													<BirthDay birth={u.birth} />
												</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.linkColumn} ${classes.iconDataColumn}`}
												>
													{!isMember(findGroupAuthByKey(groupData[0].loginUser)) && (
														<Link
															to={{
																pathname: RoutePath.MEMBER_INFO,
																state: {
																	groupId: groupData[0].group_id,
																	loginUser: groupData[0].loginUser,
																	targetUser: u,
																},
															}}
														>
															<span className={classes.linkToMemberInfo}>&gt;</span>
														</Link>
													)}
												</div>
											</div>
											<Hr />
										</>
									);
								})}
							</div>
							{!isMember(findGroupAuthByKey(groupData[0].loginUser)) && (
								<div className={'btn-box ' + classes.memberBtnBox}>
									{(isAdmin(findGroupAuthByKey(groupData[0].loginUser)) ||
										groupData[0].loginUser.member_updatable_flg === 1) && (
										<div className={'btn gr ' + classes.memberBtnBoxAddBtn}>
											<Link to={{ pathname: RoutePath.USER_ADD, state: { groupId: groupData[0].group_id } }}>
												<span>追加</span>
											</Link>
										</div>
									)}
									<div className={'btn ' + classes.memberBtnBoxLeaveBtn}>
										<Link to={{ pathname: RoutePath.LEAVE_FAMILY, state: { group: groupData[0] } }}>
											<span>「家族」から抜ける</span>
										</Link>
									</div>
								</div>
							)}
						</div>
					</>
				) : (
					<>
						<div className="section-in">
							<div className="section-head">
								<span className={classes.sectionHeadH1}>「家族」 一覧</span>
							</div>
							<div className="section-head">
								<span className={classes.sectionHeadH2}>所属しているユーザーはまだいません。</span>
							</div>
							<div className="info-box">
								<div>「追加」ボタンから既存ユーザーを招待したり、</div>
								<div>{`${GroupAuth.MEMBER.name}（メールアドレスを持っていないユーザー）の登録ができます。`}</div>
							</div>
							<div className={'btn-box'}>
								<div className={'btn gr'}>
									<Link to={RoutePath.USER_ADD}>
										<span>追加</span>
									</Link>
								</div>
							</div>
						</div>
					</>
				)}
			</section>
		</>
	);
});
